// frontend/src/pages/StudentVideoHistory.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_URL } from "../config";
import {
  Container,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  Divider,
  LinearProgress,
  Chip,
  Tooltip,
  Button,
  Paper, 
  Grid,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  PlayCircleOutline,
  PauseCircleOutline,
  SkipNext,
  EditNote,
  StopCircleOutlined,
  ReplayOutlined,
  BarChart,
} from "@mui/icons-material";
import { computeTotalWatchTime } from "../utils/watchUtils";
import WatchGanttChart from "../components/WatchGanttChart";

const formatTime = (secs) => {
  if (secs === null || secs === undefined || isNaN(secs)) return "N/A";
  if (secs === 0) return "0s";
  const h = Math.floor(secs / 3600),
    m = Math.floor((secs % 3600) / 60),
    s = Math.floor(secs % 60);
  return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s}s`;
};

const formatTimestampJST = (iso) => {
  if (!iso) return "N/A";
  try {
    const date = new Date(iso.endsWith("Z") ? iso : iso + "Z");
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch(e) {
    console.error("Error formatting timestamp in StudentVideoHistory:", iso, e);
    return "N/A";
  }
};

function renderOperation(ev) {
  const pos = ev.played_seconds ?? 0;
  let icon = null;
  let text = "";
  let chipColor = "default";

  switch (ev.event_type) {
    case "start":
      icon = <PlayCircleOutline sx={{ verticalAlign: "bottom", mr: 0.5 }} />;
      text = `再生開始 @ ${formatTime(pos)}`;
      chipColor = "success";
      break;
    case "resume":
      icon = <ReplayOutlined sx={{ verticalAlign: "bottom", mr: 0.5 }} />;
      text = `再開 @ ${formatTime(pos)}`;
      chipColor = "info";
      break;
    case "pause":
      icon = <PauseCircleOutline sx={{ verticalAlign: "bottom", mr: 0.5 }} />;
      text = `一時停止 @ ${formatTime(pos)}`;
      chipColor = "warning";
      break;
    case "seek":
      icon = <SkipNext sx={{ verticalAlign: "bottom", mr: 0.5 }} />;
      text = `シーク @ ${formatTime(pos)}`;
      chipColor = "default";
      break;
    case "end":
      icon = <StopCircleOutlined sx={{ verticalAlign: "bottom", mr: 0.5 }} />;
      text = `再生終了 @ ${formatTime(pos)}`;
      chipColor = "error";
      break;
    case "note":
      icon = <EditNote sx={{ verticalAlign: "bottom", mr: 0.5 }} />;
      text = `ノート @ ${formatTime(pos)}`;
      chipColor = "primary";
      if (ev.note_text) {
        return (
          <Tooltip title={ev.note_text} placement="top-start">
            <span>
              {icon}
              {text}: {ev.note_text.substring(0, 30)}
              {ev.note_text.length > 30 ? "..." : ""}
            </span>
          </Tooltip>
        );
      }
      break;
    default:
      text = `${ev.event_type} @ ${formatTime(pos)}`;
  }
  return (
    <>
      {icon}
      <Chip
        label={ev.event_type}
        size="small"
        color={chipColor}
        sx={{ mr: 1 }}
      />
      {text}
    </>
  );
}

const groupByVideo = (events) => {
  if (!events || !Array.isArray(events)) return {};
  return events.reduce((map, ev) => {
    if (ev && ev.video_id !== undefined) {
     (map[ev.video_id] ||= []).push(ev);
    }
    return map;
  }, {});
}

export default function StudentVideoHistory() {
  const [grouped, setGrouped] = useState({});
  const [openVideos, setOpenVideos] = useState({});
  const [message, setMessage] = useState("");
  const [allEvents, setAllEvents] = useState([]);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      setMessage("ログイン情報がありません。再ログインしてください。");
      return;
    }
    axios
      .get(`${API_URL}/student/logs`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : data.logs ?? data.data ?? [];
        setAllEvents(arr);
        const groupedData = groupByVideo(arr);
        setGrouped(groupedData);
      })
      .catch((err) => {
        console.error("StudentVideoHistory: 視聴履歴の取得エラー:", err);
        setMessage(err.response?.data?.detail || err.message || "視聴履歴の取得に失敗しました。");
        setGrouped({});
        setAllEvents([]);
      });
  }, [token]);

  const handleToggle = (videoId) => {
    setOpenVideos((prev) => ({
      ...prev,
      [videoId]: !prev[videoId],
    }));
  };

  const videoSummaries = useMemo(() => {
    return Object.entries(grouped).map(([vid, evs]) => {
        if (!Array.isArray(evs)) evs = []; // evsが配列であることを保証
        const totalSec = computeTotalWatchTime(evs);
        
        const videoData = evs && evs.length > 0 ? evs[0] : {};
        let videoDuration = null;
        if (videoData.video_duration !== null && videoData.video_duration !== undefined) {
            const parsedDur = parseFloat(videoData.video_duration);
            if (!isNaN(parsedDur)) {
                videoDuration = parsedDur;
            }
        }

        const progress =
          videoDuration && totalSec > 0 && videoDuration > 0
            ? Math.min(100, Math.round((totalSec / videoDuration) * 100))
            : 0;
      
        let lastTs = null;
        if (evs && evs.length > 0) {
            const validTimestamps = evs.map(e => e.timestamp ? new Date(e.timestamp.endsWith("Z") ? e.timestamp : e.timestamp + "Z").getTime() : 0).filter(ts => !isNaN(ts));
            if (validTimestamps.length > 0) {
                lastTs = new Date(Math.max(...validTimestamps)).toISOString();
            }
        }

        return {
          videoId: vid,
          title: videoData.video_title || `Video ID: ${vid}`,
          totalTime: totalSec,
          progress,
          lastTs,
          events: evs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
          videoDuration,
        };
    });
  }, [grouped]);


  const overallSummary = useMemo(() => {
    const totalWatchTimeAcrossAllVideos = videoSummaries.reduce(
      (sum, video) => sum + (video.totalTime || 0),
      0
    );
    const videosWithDuration = videoSummaries.filter(v => v.videoDuration && v.videoDuration > 0);
    const averageProgressVal = 
        videosWithDuration.length > 0
          ? videosWithDuration.reduce((sum, v) => sum + (v.progress || 0), 0) /
            videosWithDuration.length
          : 0;
    return {
      totalWatchTime: totalWatchTimeAcrossAllVideos,
      watchedVideosCount: videoSummaries.filter((v) => v.totalTime > 0).length,
      completedVideosCount: videoSummaries.filter((v) => v.progress >= 95).length,
      averageProgress: isNaN(averageProgressVal) ? 0 : averageProgressVal,
    };
  }, [videoSummaries]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">マイ視聴履歴</Typography>
        <Button component={Link} to="/student/home" variant="outlined">学習ページへ戻る</Button>
      </Box>

      {message && (<Typography color="error" sx={{ mb: 2 }}>{message}</Typography>)}

      {allEvents.length > 0 && !message && (
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            <BarChart sx={{ mr: 1 }} /> <strong>学習状況サマリー (全動画合計)</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><Typography variant="body1"><strong>総視聴時間:</strong> {formatTime(overallSummary.totalWatchTime)}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body1"><strong>視聴済み動画数:</strong> {overallSummary.watchedVideosCount}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body1"><strong>完了動画数 (目安95%):</strong> {overallSummary.completedVideosCount}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body1"><strong>平均進捗率:</strong> {overallSummary.averageProgress.toFixed(1)}%</Typography></Grid>
          </Grid>
        </Paper>
      )}
      
      {videoSummaries.length === 0 && !message && allEvents.length > 0 && (
        <Typography color="text.secondary">視聴ログはありますが、集計できる動画単位のデータがありません。</Typography>
      )}
      {allEvents.length === 0 && !message && (
        <Typography color="text.secondary" sx={{textAlign: 'center', py: 3}}>視聴ログがまだありません。</Typography>
      )}

      <List>
        {videoSummaries
          .sort((a, b) => {
             const dateA = a.lastTs ? new Date(a.lastTs).getTime() : 0;
             const dateB = b.lastTs ? new Date(b.lastTs).getTime() : 0;
             return dateB - dateA;
          })
          .map(
            (v) => {
              const videoDurationIsValid = v.videoDuration && typeof v.videoDuration === 'number' && v.videoDuration > 0;
              const showGanttForThisVideo = videoDurationIsValid && v.events && v.events.length > 0;
              
              return (
              <Box key={v.videoId} sx={{ mb: 2, border: "1px solid #ddd", borderRadius: 2, overflow: "hidden" }}>
                <ListItem button onClick={() => handleToggle(v.videoId)} sx={{ display: "flex", alignItems: "center", p: 2, bgcolor: "action.hover" }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{v.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      合計視聴: {formatTime(v.totalTime)}
                      {v.videoDuration && ` / 動画時間: ${formatTime(v.videoDuration)}`}
                      {v.lastTs && ` ／ 最終視聴: ${formatTimestampJST(v.lastTs)}`}
                    </Typography>
                    {videoDurationIsValid && (
                      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                        <Box sx={{ width: "100%", mr: 1 }}><LinearProgress variant="determinate" value={v.progress} sx={{ height: 8, borderRadius: 4 }}/></Box>
                        <Box sx={{ minWidth: 35 }}><Typography variant="body2" color="text.secondary">{`${v.progress}%`}</Typography></Box>
                      </Box>
                    )}
                  </Box>
                  <IconButton size="small">{openVideos[v.videoId] ? <ExpandLess /> : <ExpandMore />}</IconButton>
                </ListItem>

                <Collapse in={openVideos[v.videoId]} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2 }}>
                    {showGanttForThisVideo && (
                      <Box sx={{ mb: 2, mt: 1 }}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{mb: 0.5}}>
                          視聴パターン (ガントチャート):
                        </Typography>
                        <WatchGanttChart logs={v.events} videoDuration={v.videoDuration} />
                      </Box>
                    )}
                    
                    {v.events && v.events.length > 0 && (
                       <Typography variant="caption" display="block" color="text.secondary" sx={{mb: 0.5, mt: (showGanttForThisVideo ? 2 : 0)}}>
                          詳細イベントログ:
                        </Typography>
                    )}
                    <List component="div" disablePadding dense>
                      {v.events && v.events.map((e) => (
                        <ListItem key={e.id} sx={{ py: 0.5, display: "flex", justifyContent: "space-between", borderTop: "1px solid #eee", "&:first-of-type": { borderTop: "none" } }}>
                          <Typography variant="body2" component="div">{renderOperation(e)}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatTimestampJST(e.timestamp)}</Typography>
                        </ListItem>
                      ))}
                      {(!v.events || v.events.length === 0) && (
                        <ListItem><Typography variant="body2" color="text.secondary">詳細なイベントログはありません。</Typography></ListItem>
                      )}
                    </List>
                  </Box>
                </Collapse>
              </Box>
            )}
          )}
      </List>
    </Container>
  );
}